// ── Jira issue picker (C1450) ──
//
// Web SPA New Task modal's Jira issue picker, reachable from the Import ▾ ▸ From Jira row
// (api/web/src/pages/board.js's openCreateTaskModal(), via lib/embed-menu.js's
// imports/onImport option — same wiring point as the Trello card picker and Gmail thread
// picker). Structure mirrors gmail-thread-picker.js: resolve -> connect -> waiting ->
// search+sortable list -> preview -> pick. Overlay chrome shared via
// lib/import-picker-shell.js.

import { api } from '../api.js';
import { escapeHtml, formatDate } from '../utils.js';
import { createImportPicker, notConfiguredFrom, setupNoticeHtml } from '../lib/import-picker-shell.js';
import { capToColumn as capToColumnBytes } from '../lib/import-text.js';

const esc = escapeHtml;

const TRUNCATION_NOTICE = '\n\n_…description truncated at 60 KB. Open the issue in Jira for the rest._';

export function capToColumn(text) {
  return capToColumnBytes(text, TRUNCATION_NOTICE);
}

const JIRA_ERRORS = {
  not_connected: 'Jira is not connected.',
  reauth_required: 'Your Jira connection expired — reconnect to continue.',
  issue_not_found: 'That issue is no longer available in Jira.',
  issue_forbidden: 'You do not have permission to view that issue.',
  invalid_query: 'Jira could not parse that search.',
  invalid_issue_key: 'That does not look like a valid Jira issue key.',
  rate_limited: 'Jira is rate-limiting requests — try again in a moment.',
  // C1551 — plain-prose fallback for the two secondary call sites (issue list/detail) that
  // keep their existing inline-banner styling; the two primary entry points (resolve,
  // connect) render the fuller setupNoticeHtml() notice with the actual missing var names.
  jira_not_configured: "Jira import isn't configured on this server yet.",
};

// api.js sets err.message = body.error, and routes/jira.js returns machine tokens as that
// field — a naive err.message fallback would print the token itself. Mirrors
// gmail-thread-picker.js's gmailErrorMessage(), not the Trello picker's (which has the
// identical latent bug of printing a raw token, left unfixed there).
function jiraErrorMessage(err, fallback) {
  const code = err && err.body && err.body.error;
  if (code && JIRA_ERRORS[code]) return JIRA_ERRORS[code];
  // jira_not_configured (C1551) is also >= 500 (503) but handled separately via
  // notConfiguredFrom() at each call site — checked before this fallback ever runs.
  if (err && err.status >= 500) return 'Jira request failed — your connection may have expired.';
  return fallback;
}

// C1551: a not-configured 503 isn't reconnectable — there is no connection to reconnect,
// the server itself is missing JIRA_* env vars. Offering "Reconnect Jira" there would be a
// dead-end loop (auth/initiate just 503s again).
function isReconnectable(err) {
  return !!(err && err.status >= 500) && !notConfiguredFrom(err);
}

const SORT_DEFAULT_DIR = { key: 'asc', summary: 'asc', status: 'asc', updated: 'desc' };
const SORT_LABELS = { key: 'Key', summary: 'Summary', status: 'Status', updated: 'Updated' };

function sortIssues(issues, key, dir) {
  const mul = dir === 'asc' ? 1 : -1;
  const arr = issues.slice();
  arr.sort((a, b) => {
    if (key === 'updated') {
      const av = Date.parse(a.updated || '');
      const bv = Date.parse(b.updated || '');
      const aNaN = Number.isNaN(av), bNaN = Number.isNaN(bv);
      if (aNaN && bNaN) return 0;
      if (aNaN) return 1; // unparseable dates sort last regardless of direction
      if (bNaN) return -1;
      return (av - bv) * mul;
    }
    return String(a[key] || '').toLowerCase().localeCompare(String(b[key] || '').toLowerCase()) * mul;
  });
  return arr;
}

function headHtml(sortKey, sortDir) {
  return Object.keys(SORT_LABELS).map((key) => {
    const active = key === sortKey;
    const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    return `<button type="button" class="jira-th${active ? ' is-active' : ''}" data-sort="${key}" aria-sort="${active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}">${esc(SORT_LABELS[key])}${arrow}</button>`;
  }).join('');
}

function issueRowsHtml(issues) {
  if (!issues.length) return '';
  return issues.map((i) => `
    <div class="jira-issue-row" data-key="${esc(i.key)}" tabindex="0" role="button">
      <span class="jira-issue-key">${esc(i.key)}</span>
      <span class="jira-issue-summary">${esc(i.summary || '(no summary)')}</span>
      <span class="jira-issue-status">${esc(i.status || '')}</span>
      <span class="jira-issue-updated">${esc(formatDate(i.updated))}</span>
    </div>`).join('');
}

// openJiraIssuePicker({ onPick }) -> void
//   onPick({ title, description }) — called once, right before the overlay closes itself.
export function openJiraIssuePicker({ onPick } = {}) {
  const ui = createImportPicker({ title: 'Import from Jira' });
  const { body, setBody, statusHtml, errorHtml, showInlineError, onWindowFocusOnce, close } = ui;

  const state = { onPick, query: '', issues: [], sortKey: 'updated', sortDir: 'desc' };
  let searchTimer = null;
  let latestSeq = 0;

  // ── Step 0: resolve connection ──
  async function renderResolveStep() {
    setBody(statusHtml('Checking connection…'));
    let statusRes;
    try {
      statusRes = await api.get('/jira/status');
    } catch (err) {
      if (ui.destroyed) return;
      const notConfigured = notConfiguredFrom(err);
      setBody(notConfigured ? setupNoticeHtml(notConfigured) : errorHtml(jiraErrorMessage(err, 'Failed to check Jira connection.')));
      return;
    }
    if (ui.destroyed) return;

    if (!statusRes.connected || statusRes.needs_reauth) { renderConnectStep(statusRes.needs_reauth); return; }
    renderSearchStep(true);
  }

  // ── Step 1: connect ──
  function renderConnectStep(reauth) {
    setBody(`
      <p style="margin:0 0 12px">${reauth ? 'Your Jira connection expired.' : 'Connect your Jira account to import an issue.'}</p>
      <p style="margin:0 0 16px;font-size:.85rem;color:var(--c-overlay1)">Opens Jira sign-in in a new tab — this window stays open.</p>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="jira-picker-cancel">Cancel</button>
        <button class="btn btn-primary" id="jira-picker-connect">${reauth ? 'Reconnect Jira' : 'Connect Jira'}</button>
      </div>`);

    body.querySelector('#jira-picker-cancel').addEventListener('click', close);
    body.querySelector('#jira-picker-connect').addEventListener('click', async () => {
      const btn = body.querySelector('#jira-picker-connect');
      btn.disabled = true; btn.textContent = 'Opening…';
      // Open the tab synchronously, inside the click gesture — Safari/Firefox block
      // window.open() called after an await (the user-gesture chain is already broken by
      // then), so the real navigation target is assigned once the URL comes back.
      const w = window.open('', '_blank');
      try {
        const { url } = await api.get('/jira/auth/initiate');
        if (ui.destroyed) { w?.close(); return; }
        if (w) { w.opener = null; w.location = url; }
        renderWaitingStep(url);
      } catch (err) {
        w?.close();
        if (ui.destroyed) return;
        const notConfigured = notConfiguredFrom(err);
        if (notConfigured) { setBody(setupNoticeHtml(notConfigured)); return; }
        btn.disabled = false; btn.textContent = reauth ? 'Reconnect Jira' : 'Connect Jira';
        showInlineError(jiraErrorMessage(err, 'Failed to start Jira authorization.'));
      }
    });
  }

  function renderWaitingStep(url) {
    setBody(`
      <p style="margin:0 0 12px">Finish authorizing in the other tab, then come back here.</p>
      <p style="margin:0 0 16px;font-size:.85rem"><a href="${esc(url)}" target="_blank" rel="noopener">Reopen the Jira authorization page</a></p>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="jira-picker-cancel">Cancel</button>
        <button class="btn btn-primary" id="jira-picker-retry">I've connected — Continue</button>
      </div>`);
    body.querySelector('#jira-picker-cancel').addEventListener('click', close);
    body.querySelector('#jira-picker-retry').addEventListener('click', renderResolveStep);

    // Auto-retry once the user tabs back to this window.
    onWindowFocusOnce(renderResolveStep);
  }

  // ── Step 2: search + sortable list ──
  // fetchOnMount: true for the initial entry from Step 0; false when returning here via
  // the preview step's Back button, which repaints cached state.issues with no refetch.
  function renderSearchStep(fetchOnMount) {
    setBody(`
      <div class="form-group">
        <input class="form-input" id="jira-picker-search" type="search" autocomplete="off"
               placeholder="Search Jira (e.g. ABC-123, or free text)" value="${esc(state.query)}">
      </div>
      <div class="jira-issue-table">
        <div class="jira-issue-head" id="jira-picker-head">${headHtml(state.sortKey, state.sortDir)}</div>
        <div class="jira-issue-list" id="jira-picker-list"></div>
      </div>`);

    const searchInput = body.querySelector('#jira-picker-search');
    const head = body.querySelector('#jira-picker-head');

    searchInput.addEventListener('input', () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => fetchIssues(searchInput.value.trim()), 300);
    });

    head.addEventListener('click', (e) => {
      const th = e.target.closest('.jira-th');
      if (!th) return;
      const key = th.dataset.sort;
      if (state.sortKey === key) {
        state.sortDir = state.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        state.sortKey = key;
        state.sortDir = SORT_DEFAULT_DIR[key] || 'asc';
      }
      head.innerHTML = headHtml(state.sortKey, state.sortDir);
      renderRows();
    });

    body.querySelector('#jira-picker-list').addEventListener('click', (e) => {
      const list = body.querySelector('#jira-picker-list');
      const row = e.target.closest('.jira-issue-row');
      if (!row || list.classList.contains('is-busy')) return;
      pickIssue(row.dataset.key, row);
    });

    if (fetchOnMount) fetchIssues(state.query);
    else renderRows();
  }

  function renderRows() {
    const list = body.querySelector('#jira-picker-list');
    if (!list) return; // step moved on already
    if (!state.issues.length) {
      list.innerHTML = statusHtml(state.query ? 'No issues match that search.' : 'No recent issues found.');
      return;
    }
    list.innerHTML = issueRowsHtml(sortIssues(state.issues, state.sortKey, state.sortDir));
  }

  async function fetchIssues(q) {
    const seq = ++latestSeq;
    const list = body.querySelector('#jira-picker-list');
    if (list) list.innerHTML = statusHtml('Searching…');

    let issues;
    try {
      issues = await api.get('/jira/issues?limit=50' + (q ? '&q=' + encodeURIComponent(q) : ''));
    } catch (err) {
      if (ui.destroyed || seq !== latestSeq) return;
      const code = err && err.body && err.body.error;
      if (code === 'not_connected' || code === 'reauth_required') { renderConnectStep(code === 'reauth_required'); return; }
      const freshList = body.querySelector('#jira-picker-list');
      if (freshList) {
        freshList.innerHTML = errorHtml(jiraErrorMessage(err, 'Failed to load issues.')) +
          (isReconnectable(err) ? `<div style="text-align:center;padding:0 0 12px"><button class="btn btn-ghost btn-sm" id="jira-picker-reconnect">Reconnect Jira</button></div>` : '');
        freshList.querySelector('#jira-picker-reconnect')?.addEventListener('click', () => renderConnectStep(true));
      }
      return;
    }
    if (ui.destroyed || seq !== latestSeq) return;

    state.query = q;
    // NOTE: /jira/issues returns a bare JSON array, not { issues }.
    state.issues = Array.isArray(issues) ? issues : [];
    renderRows();
  }

  async function pickIssue(issueKey, row) {
    clearTimeout(searchTimer); searchTimer = null;
    latestSeq++; // discard any /issues response still on the wire — it must not repaint under this pick
    const list = body.querySelector('#jira-picker-list');
    list.classList.add('is-busy');
    row.classList.add('is-picking');

    let detail;
    try {
      detail = await api.get('/jira/issues/' + encodeURIComponent(issueKey));
    } catch (err) {
      if (ui.destroyed) return;
      list.classList.remove('is-busy');
      row.classList.remove('is-picking');
      showInlineError(jiraErrorMessage(err, 'Failed to load issue.'));
      return;
    }
    if (ui.destroyed) return;

    renderPreviewStep(detail);
  }

  // ── Step 3: preview ──
  function renderPreviewStep(detail) {
    const description = buildIssueText(detail); // exactly what "Use this issue" will insert
    const sizeBytes = new TextEncoder().encode(description).length;
    const truncated = description.includes('truncated at 60 KB');
    const subLine = `${detail.status || ''} · ${detail.issue_type || ''}${truncated ? ' · (truncated)' : ''} · ${(sizeBytes / 1024).toFixed(1)} KB`;

    // Preview renders escaped into a <pre>, never renderMarkdown() — utils.js's
    // renderMarkdown() is a bare marked.parse() with no sanitizer (same rule
    // gmail-thread-picker.js follows, for the same reason).
    setBody(`
      <div class="import-picker-preview-head">
        <div class="import-picker-preview-title">${esc(detail.key)} ${esc(detail.summary || '(no summary)')}</div>
        <div class="import-picker-preview-sub">${esc(subLine)}</div>
      </div>
      <pre class="jira-issue-preview-body">${esc(detail.description || '(no description)')}</pre>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="jira-picker-back">Back</button>
        <button class="btn btn-primary" id="jira-picker-use">Use this issue</button>
      </div>`);

    body.querySelector('#jira-picker-back').addEventListener('click', () => renderSearchStep(false));
    body.querySelector('#jira-picker-use').addEventListener('click', () => {
      const payload = { title: `${detail.key} ${detail.summary || ''}`.trim(), description };
      close();
      if (state.onPick) state.onPick(payload);
    });
  }

  renderResolveStep();
}

// buildIssueText(detail) -> string
// Provenance line + a compact metadata line, then the issue's Markdown description
// (already converted server-side from ADF — see api/src/lib/adf-to-markdown.js), capped
// to the tasks.description column budget.
export function buildIssueText(detail) {
  const meta = [
    detail.status ? `**Status:** ${detail.status}` : '',
    detail.issue_type ? `**Type:** ${detail.issue_type}` : '',
    detail.priority ? `**Priority:** ${detail.priority}` : '',
    detail.assignee ? `**Assignee:** ${detail.assignee}` : '',
  ].filter(Boolean).join(' · ');

  const parts = [`**Jira issue:** ${detail.url || ''}`];
  if (meta) parts.push(meta);
  if (detail.description) parts.push(detail.description);
  return capToColumn(parts.join('\n\n'));
}
